import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface CustomCalendarProps {
    value: string;
    onChange: (val: string) => void;
    onClose: () => void;
    inline?: boolean;
}

export const CustomCalendar: React.FC<CustomCalendarProps> = ({ value, onChange, onClose, inline }) => {
    const { t } = useTranslation();
    const [viewDate, setViewDate] = useState(new Date(value || new Date()));
    const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const days = daysInMonth(year, month);
    const firstDay = firstDayOfMonth(year, month);

    const monthNames = [
        t('controller.rundownEditor.months.january'), t('controller.rundownEditor.months.february'),
        t('controller.rundownEditor.months.march'), t('controller.rundownEditor.months.april'),
        t('controller.rundownEditor.months.may'), t('controller.rundownEditor.months.june'),
        t('controller.rundownEditor.months.july'), t('controller.rundownEditor.months.august'),
        t('controller.rundownEditor.months.september'), t('controller.rundownEditor.months.october'),
        t('controller.rundownEditor.months.november'), t('controller.rundownEditor.months.december')
    ];

    const handleDateSelect = (d: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const newDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        onChange(newDate);
        onClose();
    };

    const containerStyle = inline ? {} : {
        position: 'absolute' as const, top: '100%', left: 0, zIndex: 100, marginTop: '10px',
        background: 'var(--color-bg-base)', border: '1px solid var(--color-panel-border)',
        padding: '15px', borderRadius: 'var(--border-radius-md)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
        width: '280px'
    };

    return (
        <div style={containerStyle} onMouseDown={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <button onMouseDown={(e) => { e.stopPropagation(); setViewDate(new Date(year, month - 1)); }} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: '1.2rem' }}>&lsaquo;</button>
                <span style={{ fontWeight: '900', fontSize: '0.8rem', letterSpacing: '2px', color: 'var(--color-data-rundown)' }}>{monthNames[month]} {year}</span>
                <button onMouseDown={(e) => { e.stopPropagation(); setViewDate(new Date(year, month + 1)); }} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', fontSize: '1.2rem' }}>&rsaquo;</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '5px', textAlign: 'center' }}>
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={`${d}-${i}`} style={{ fontSize: '0.6rem', color: 'var(--color-text-muted)', fontWeight: 'bold' }}>{d}</div>)}
                {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
                {Array.from({ length: days }).map((_, i) => {
                    const d = i + 1;
                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                    const isSelected = value === dateStr;
                    return (
                        <div
                            key={d}
                            onMouseDown={(e) => handleDateSelect(d, e)}
                            style={{
                                padding: '8px 0', fontSize: '0.75rem', cursor: 'pointer', borderRadius: '4px',
                                color: isSelected ? '#000' : 'var(--color-text-primary)',
                                background: isSelected ? 'var(--color-data-rundown)' : 'transparent',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={e => !isSelected && (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                            onMouseLeave={e => !isSelected && (e.currentTarget.style.background = 'transparent')}
                        >
                            {d}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
